import { useMemo } from "react";
import type { EndUserRole, SystemRole } from "@adva/auth";
import { useAuth } from "@/contexts/auth-context";

interface CurrentUser {
  userId: string;
  email: string;
  displayName: string;
  accountId: string;
  businessId: string;
  accountRole: string;
  businessRole: string;
  // ADV-879: unified-role fields. Computed from JWT app_metadata (new claims
  // populated by custom_access_token_hook after Phase 4; legacy claims mapped
  // until then). Used by AbilityContext to drive permission checks.
  systemRole: SystemRole;
  endUserRole: EndUserRole | null;
}

// ADV-1034: safety net for JWTs minted before the hook rewrite landed.
// Those tokens may still carry `account_role: "owner"`. New JWTs no longer
// produce "owner" — the API rejects it (AccountRoleSchema) and the DB CHECK
// constraint excludes it since ADV-1063. The owner→admin branch can be
// collapsed once all in-flight tokens have expired (~1 hr per session).
function mapLegacyAccountRoleToSystemRole(role: string): SystemRole {
  if (role === "super_admin") return "super_admin";
  if (role === "owner" || role === "admin") return "admin";
  if (role === "billing") return "billing";
  return "user";
}

function decodeJwtClaims(
  accessToken: string | undefined,
): Record<string, unknown> | null {
  if (!accessToken) return null;
  try {
    return JSON.parse(atob(accessToken.split(".")[1]));
  } catch {
    return null;
  }
}

export function useCurrentUser(): CurrentUser | null {
  const { session, user } = useAuth();

  return useMemo(() => {
    if (!session || !user) return null;

    const claims = decodeJwtClaims(session.access_token);
    const meta = (claims?.app_metadata ?? {}) as Record<string, string>;

    const accountRole = meta.account_role ?? "viewer";
    const businessRole = meta.business_role ?? "viewer";

    // ADV-879: prefer new claims when present; fall back to legacy mapping until
    // the JWT hook (Phase 4) writes system_role / end_user_role directly.
    const systemRole =
      (meta.system_role as SystemRole | undefined) ??
      mapLegacyAccountRoleToSystemRole(accountRole);
    const endUserRole: EndUserRole | null =
      (meta.end_user_role as EndUserRole | undefined) ??
      (systemRole === "user" ? "manager" : null);

    return {
      userId: user.id,
      email: user.email ?? "",
      displayName: user.user_metadata?.full_name || user.email || "User",
      accountId: meta.account_id ?? "",
      businessId: meta.business_id ?? "",
      accountRole,
      businessRole,
      systemRole,
      endUserRole,
    };
  }, [session, user]);
}
